/* Route transitions.

   Direction-aware: going deeper slides in from the leading edge, going back
   slides out to it. The direction is derived from the router's history index
   rather than from path depth, because `/basket → /checkout` and `/checkout →
   /basket` are the same two paths and only the history tells them apart.

   Deliberately small — 16px and 180ms. A page transition is the animation a
   user sees most often in a session, and anything showier turns into a tax on
   every single navigation. It exists to say "this replaced that", not to be
   admired.

   `mode="popLayout"` rather than `wait`: waiting for the outgoing page to
   finish before starting the incoming one doubles the perceived latency of
   every tap. */

import { useEffect, useRef, type ReactNode } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { EASE_OUT } from '../../design/motion'

export function PageTransition({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navType = useNavigationType()
  const reduce = useReducedMotion()

  /* POP is the browser's back/forward. PUSH and REPLACE are forward motion.
     Keyed off the router rather than a guess at hierarchy. */
  const back = navType === 'POP'

  /* Restore scroll on forward navigation only. Coming back should land where
     the user left, which the browser already does for POP — overriding it is
     the single most annoying thing a SPA router does. */
  const prevKey = useRef(location.key)
  useEffect(() => {
    if (prevKey.current !== location.key && navType !== 'POP') {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
    }
    prevKey.current = location.key
  }, [location.key, navType])

  if (reduce) {
    return (
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    )
  }

  const offset = back ? -16 : 16

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={location.pathname}
        /* `x` here rather than a transform string is a deliberate exception:
           the value is small, the element is a full page, and Motion needs the
           numeric form to interrupt cleanly when a user taps twice quickly. */
        initial={{ opacity: 0, x: offset }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -offset }}
        transition={{ duration: 0.18, ease: EASE_OUT }}
        /* No `will-change` here, deliberately.

           A `will-change: transform` that is never removed is not a hint, it
           is a permanent full-page compositor layer — and, worse, it makes
           this wrapper the containing block for every `position: fixed`
           descendant for the entire life of the route, not just the 180ms it
           is animating. A page's own fixed chrome then anchors to the page
           instead of the viewport and comes to rest at the bottom of the
           content. Motion promotes the layer for the duration of the
           animation on its own; that is the whole window in which the hint
           would have been worth anything. */
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}
